import React, { useId, useState } from 'react';
import { TopicCategoryPicker } from './TopicCategoryPicker';
import { PhotoIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { MindBookFormData, MindBookChapterInput } from '../../types/mindbook';
import { ContentStatus, STATUS_ANZEIGE, STATUS_WAEHLBAR } from '../../types';
import { BunnyFilePicker } from '../admin/BunnyFilePicker';
import { parseTimecodeToMs, formatMsToTimecode } from '../../utils/markersParser';
import { FassungsUebersicht } from '../content/FassungsUebersicht';
import { MINDBOOK_ETIKETTEN } from '../../types/fassungen';
import { useFassungen } from '../../hooks/useFassungen';
import { SchlagwortFeld } from './SchlagwortFeld';
import { ModerationsHinweis } from './ModerationsHinweis';
import { PremiumFeld } from './PremiumFeld';
import { Rueckfrage } from '../ui/Rueckfrage';
import { useRueckfrage } from '../../hooks/useRueckfrage';
import { useDarfModerieren } from '../../hooks/useDarfModerieren';
import { fälltAufEntwurf, MODERATIONS_TEXTE, RÜCKFALL_RÜCKFRAGE, stufeGesperrt, stufenText } from '../../utils/moderation';

interface MindBookFormProps {
  initialData?: Partial<MindBookFormData>;
  onSubmit: (data: MindBookFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  mindBookId?: string;
  onDelete?: (id: string) => void;
}

// Interne Kapitel-Zeile: Zeit als editierbarer String (mm:ss / h:mm:ss), beim Submit → startMs.
interface ChapterRow {
  title: string;
  timeText: string;
}

const buildForm = (d: Partial<MindBookFormData>): MindBookFormData => ({
  title: d.title || '',
  author: d.author || '',
  description: d.description || '',
  shortDescription: d.shortDescription || '',
  keywords: d.keywords || '',
  duration: d.duration || '',
  coverImageUrl: d.coverImageUrl || '',
  // ⚠️ `markerTimecodeCSVUrl` steht hier NICHT mehr (2026-09-08, Punkt 7). Es hatte nie ein
  // Eingabefeld — das Formular lud es und schickte es unveraendert zurueck.
  // ✅ Der Wegfall LOESCHT nichts: `mindBookMapping.ts:76-81` liest das Feld ausdruecklich NUR
  // aus dem Bestand und ignoriert die Nutzlast („Loeschung durch Auslassung" ist dort benannt
  // und abgestellt). Nachgemessen, nicht angenommen.
  isPremium: d.isPremium ?? true,
  tags: d.tags || [],
  topicCategories: d.topicCategories || [],
  status: d.status || ContentStatus.DRAFT,
});

export const MindBookForm: React.FC<MindBookFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  mindBookId,
  onDelete,
}) => {
  // Siehe `ui/Filterleiste.tsx`: erzeugte Wurzel statt fester Zeichenketten.
  const feldId = useId();
  // ⚠️ Der Haken sitzt HIER und nicht in der Seite — anders als bei Audio, und mit Grund: dort
  // brauchten ZWEI Flächen dieselben Daten (Schalter-Deckung und die Liste darunter), deshalb war
  // die Seite ihr Besitzer. Hier gibt es genau einen Verbraucher; ein Zustand, der durch zwei
  // Ebenen gereicht wird, ohne dass ihn jemand zweiter liest, ist Verdrahtung ohne Nutzen.
  // Kommt später eine zweite Fläche dazu, zieht man ihn hoch.
  const fassungen = useFassungen(mindBookId ?? null, 'mindbook');

  const [formData, setFormData] = useState<MindBookFormData>(buildForm(initialData));

  // Was der Moderation gehört (Bezahlschranke, Veröffentlichen, Rückfall auf den Entwurf) — Regeln und
  // Texte in `utils/moderation.ts`. Der Bestand ist der Status, mit dem das Formular geöffnet wurde.
  const darfModerieren = useDarfModerieren();
  const bestandStatus = initialData.status;
  const rückfall = fälltAufEntwurf(darfModerieren, bestandStatus, formData.status);
  const { rückfrage, frage, antworte } = useRueckfrage();

  const handleThemaToggle = (subTopicKey: string) => {
    setFormData((prev) => {
      const aktuell = prev.topicCategories || [];
      return {
        ...prev,
        topicCategories: aktuell.includes(subTopicKey)
          ? aktuell.filter((k) => k !== subTopicKey)
          : [...aktuell, subTopicKey],
      };
    });
  };
  const [chapters, setChapters] = useState<ChapterRow[]>(
    (initialData.chapters || []).map((c) => ({ title: c.title, timeText: formatMsToTimecode(c.startMs) }))
  );
  const [coverPreview, setCoverPreview] = useState<string>(initialData.coverImageUrl || '');
  // Nur noch das Cover kommt aus Bunny — Ziel und Dateityp waren erst noetig, als die zwei
  // Ton-Adressen daneben standen (siehe den Block „Audio-Fassungen").
  const [pickerOffen, setPickerOffen] = useState(false);

  const set = <K extends keyof MindBookFormData>(key: K, value: MindBookFormData[K]) =>
    setFormData((p) => ({ ...p, [key]: value }));



  const handleBunnySelect = (url: string) => {
    set('coverImageUrl', url);
    setCoverPreview(url);
    setPickerOffen(false);
  };

  const addChapter = () => setChapters((c) => [...c, { title: `Kapitel ${c.length + 1}`, timeText: '0:00' }]);
  const removeChapter = (i: number) => setChapters((c) => c.filter((_, idx) => idx !== i));
  const updateChapter = (i: number, patch: Partial<ChapterRow>) =>
    setChapters((c) => c.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    // F3: speichert die Redaktion ein veröffentlichtes Werk, fällt es auf den Entwurf — vorher fragen,
    // nicht hinterher melden. Begründung an `RÜCKFALL_RÜCKFRAGE`.
    if (rückfall && !(await frage(RÜCKFALL_RÜCKFRAGE))) return;
    // Kapitel-Zeilen → {title, startMs}, ungültige Zeit fällt auf 0, nach Zeit sortiert.
    const parsedChapters: MindBookChapterInput[] = chapters
      .map((row, i) => ({
        title: row.title.trim() || `Kapitel ${i + 1}`,
        startMs: parseTimecodeToMs(row.timeText) ?? 0,
      }))
      .sort((a, b) => a.startMs - b.startMs);
    onSubmit({ ...formData, chapters: parsedChapters });
  };

  const inputCls = 'input w-full';
  const labelCls = 'block text-sm font-medium text-secondary-700 mb-2';

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pb-28">
      {/* Basisdaten */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-secondary-900">Basisdaten</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor={`${feldId}-titel`} className={labelCls}>Titel *</label>
            <input id={`${feldId}-titel`} type="text" value={formData.title} onChange={(e) => set('title', e.target.value)} className={inputCls} required />
          </div>
          <div>
            <label htmlFor={`${feldId}-autor`} className={labelCls}>Autor</label>
            <input
              id={`${feldId}-autor`}
              type="text"
              value={formData.author}
              onChange={(e) => set('author', e.target.value)}
              className={inputCls}
              placeholder="z.B. Neville Goddard"
            />
          </div>
        </div>
        <div>
          <label htmlFor={`${feldId}-beschreibung`} className={labelCls}>Beschreibung</label>
          <textarea id={`${feldId}-beschreibung`} value={formData.description} onChange={(e) => set('description', e.target.value)} className={inputCls} rows={3} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor={`${feldId}-kurzbeschreibung`} className={labelCls}>Kurzbeschreibung</label>
            <input id={`${feldId}-kurzbeschreibung`} type="text" value={formData.shortDescription} onChange={(e) => set('shortDescription', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor={`${feldId}-dauer`} className={labelCls}>Dauer (z.B. "1:39")</label>
            <input id={`${feldId}-dauer`} type="text" value={formData.duration} onChange={(e) => set('duration', e.target.value)} className={inputCls} />
          </div>
        </div>
      </div>

      {/* Cover */}
      <div className="card p-6 space-y-3">
        <h2 className="text-lg font-semibold text-secondary-900">Cover</h2>
        <div className="flex items-center gap-4">
          {coverPreview ? (
            <img src={coverPreview} alt="Cover" className="h-24 w-24 rounded object-cover" />
          ) : (
            <div className="h-24 w-24 rounded bg-secondary-100 flex items-center justify-center">
              <PhotoIcon className="w-8 h-8 text-secondary-400" />
            </div>
          )}
          <div className="flex-1 space-y-2">
            <input
              type="text"
              value={formData.coverImageUrl}
              onChange={(e) => { set('coverImageUrl', e.target.value); setCoverPreview(e.target.value); }}
              className={inputCls}
              placeholder="Cover-URL (oder aus Bunny wählen)"
            />
            <button type="button" onClick={() => setPickerOffen(true)} className="btn btn-outline">
              <PhotoIcon className="w-5 h-5 mr-2" /> Von Bunny.net
            </button>
          </div>
        </div>
      </div>

      {/* Audio-Fassungen */}
      <div className="card p-6 space-y-5">
        <h2 className="text-lg font-semibold text-secondary-900">Audio-Fassungen</h2>
        {/* ── Die zwei Adressfelder sind GEFALLEN (Phase 3 „Contract“, nach Toms Abnahme 2026-09-01) ──
            Hier standen „MindBook-Audio (mit Subliminals)“ und „Hörbuch-Audio (ohne Subliminals)“,
            je mit Bunny-Wähler, darunter der Satz „Eine der beiden Fassungen genügt. Sind beide
            gesetzt, erscheint im Portal der Mit/Ohne-Subliminal-Umschalter.“

            Der Satz beschrieb, seit #792 auf dev abgenommen ist, eine Mechanik, die es nicht mehr
            gibt: der Umschalter entsteht aus der Wiedergabe-Route, nicht aus diesen Feldern. Wer
            sie stehen lässt, lädt die Redaktion ein, eine Adresse zu pflegen, die nichts bewirkt —
            und das ist schlimmer als ein fehlendes Feld: es sieht nach Steuerung aus.

            ⚠️ Gemessen, nicht angenommen, bevor der Schnitt fiel: 023 trägt BEIDE Felder leer und
            spielt trotzdem — über die Kachel angetippt lief es 0:09/1:46:21 aus der signierten neuen
            Zone, null Anfragen gegen `ee-cms` (2026-09-01, Toms angemeldeter Browser). Die
            Auflösung hängt an der WERK-KENNUNG, nicht an `track.url`; eine leere Adresse ist ihr
            also kein Hindernis. Das ist genau der Zustand, in dem ab jetzt JEDES neue MindBook
            anfängt — deshalb musste er vor dem Schnitt belegt sein und nicht danach.

            ⛔ Die Bestandswerte in `metadata` bleiben liegen. Sie sind tote Daten, kein Schätzchen,
            aber ein Löschlauf wäre schreibend auf Bestand und ist ausdrücklich NICHT beauftragt.
            Der Schreibpfad setzt sie nur nicht mehr neu (`services/mindBookService.ts`). */}

        {!mindBookId && (
          <p className="text-sm text-secondary-600">
            Die Ton-Dateien kommen aus dem Ingest, nicht aus diesem Formular. Sobald das Werk
            angelegt und eingelesen ist, stehen seine Fassungen hier.
          </p>
        )}

        {/* ── Was der Ingest eingelesen hat ──────────────────────────────────────────────────
            Seit die zwei Adressfelder gefallen sind, ist diese Liste der GANZE Inhalt der Fläche:
            nicht mehr „was die Redaktion setzt“ über „was im Speicher liegt“, sondern nur noch das
            Zweite. Das ist ehrlicher, als es aussieht — gesetzt wurde hier ohnehin nichts mehr,
            was der Player gelesen hätte.
            ⚠️ `mitTonListe`, weil ein MindBook KEINE Varianten-Schalter hat: seine zwei Fassungen
            unterscheiden sich am Dateinamen (`audio.mp3` / `audio-hoerbuch.mp3`), nicht an einem
            Schalter. Ohne die Liste erschienen die eingelesenen Ton-Dateien nirgends.
            ⚠️ Nur im Bearbeiten-Fall: beim Anlegen gibt es weder Kennung noch Dateien — dafür
            steht der Satz darüber. */}
        {mindBookId && (
          <div>
            <FassungsUebersicht zustand={fassungen} mitTonListe etiketten={MINDBOOK_ETIKETTEN} />
          </div>
        )}
      </div>

      {/* Kapitel-Marker */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-secondary-900">Kapitel</h2>
          <div className="flex gap-2">
            <button type="button" onClick={addChapter} className="btn btn-outline">
              <PlusIcon className="w-5 h-5 mr-2" /> Kapitel
            </button>
          </div>
        </div>
        {/* ⚠️ Hier stand „Markers.csv importieren" (Knopf, verstecktes Datei-Feld, Fehlerzeile).
            Entfallen am 2026-09-08, Tom-Entscheid „1 bis 7 wie empfohlen" (Punkt 7). Der
            Kapitel-Editor darunter BLEIBT vollstaendig bedienbar — hinzufuegen, entfernen, Titel
            und Zeit tippen; er verliert nur das Befuellen aus einer Datei.
            ⛔ Was NICHT mitgegangen ist und warum: `utils/markersParser.ts` fuehrt drei Funktionen,
            und nur EINE gehoerte zur CSV (`parseMarkersCsv`). `parseTimecodeToMs` wandelt die von
            Hand getippte Zeit beim Speichern zurueck, `formatMsToTimecode` zeigt sie an — hier und
            in der Fassungs-Uebersicht. Die Datei zu loeschen, wie der Posten es nannte, haette
            drei Stellen gebrochen. */}
        {chapters.length === 0 ? (
          <p className="text-sm text-secondary-500">
            Noch keine Kapitel. Fügen Sie sie über „Kapitel" hinzu — Titel und Startzeit je Zeile.
          </p>
        ) : (
          <div className="space-y-2">
            {chapters.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-6 text-sm text-secondary-500 text-right">{i + 1}</span>
                <input
                  type="text"
                  value={row.title}
                  onChange={(e) => updateChapter(i, { title: e.target.value })}
                  className="input flex-1"
                  placeholder={`Kapitel ${i + 1}`}
                />
                <input
                  type="text"
                  value={row.timeText}
                  onChange={(e) => updateChapter(i, { timeText: e.target.value })}
                  className="input w-28"
                  placeholder="mm:ss"
                />
                <button type="button" onClick={() => removeChapter(i)} className="p-2 text-error-500 hover:text-error-700" title="Kapitel entfernen">
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Klassifizierung — dieselbe Stelle im Formular wie bei Audio: nach den Inhaltsdaten,
          vor der Veröffentlichung. Bewusst die GETEILTE Komponente: sie trägt die Behandlung
          verwaister und deaktivierter Slugs an genau einer Stelle. */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-secondary-900">Klassifizierung</h2>
        <div>
          <h3 className="text-sm font-medium text-secondary-700 mb-2">Themen-Kategorien</h3>
          <TopicCategoryPicker
            selected={formData.topicCategories || []}
            onToggle={handleThemaToggle}
          />
        </div>
        {/* Schlagworte — dieselbe Bedienung wie in `AudioForm` und `VideoForm`, seit dem
            2026-09-08 aus EINEM Bauteil. ⚠️ Sie fehlte hier, seit es dieses Formular gibt: das
            Feld `tags` wurde geladen (`buildForm:37`) und beim Speichern unveraendert
            zurueckgeschickt, aber nirgends angezeigt. Seit die MindBook-Detailseite die
            Schlagworte ZEIGT (21 von 21 Werken tragen je fuenf), sah die Redaktion Werte, die
            sie nicht anfassen konnte.
            Stelle: unter „Themen-Kategorien", also dieselbe Reihenfolge wie auf der
            Detailseite (Kategorie → Themen) und wie in den beiden Schwester-Formularen. */}
        <SchlagwortFeld
          werte={formData.tags || []}
          onÄndern={(tags) => setFormData((prev) => ({ ...prev, tags }))}
        />
      </div>

      {/* Veröffentlichung */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-secondary-900">Veröffentlichung</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor={`${feldId}-status`} className={labelCls}>Status</label>
            {/* Wählbar sind nur die drei Stufen aus `STATUS_WAEHLBAR` — „Abgelehnt" vergibt die
                Prüfung über `POST /api/content/:id/reject` (ADMIN/MODERATOR), nicht der Verfasser.
                Ist der Datensatz bereits abgelehnt, steht die Stufe GESPERRT dabei: ohne sie
                zeigte das Auswahlfeld eine leere Zeile, und der Zustand wäre unsichtbar. */}
            <select
              id={`${feldId}-status`}
              value={formData.status}
              onChange={(e) => set('status', e.target.value as ContentStatus)}
              aria-describedby={darfModerieren ? undefined : `${feldId}-status-hinweis`}
              className={inputCls}
            >
              {STATUS_WAEHLBAR.map((stufe) => (
                <option key={stufe} value={stufe} disabled={stufeGesperrt(stufe, darfModerieren)}>
                  {stufenText(stufe, darfModerieren, bestandStatus)}
                </option>
              ))}
              {formData.status === ContentStatus.REJECTED && (
                <option value={ContentStatus.REJECTED} disabled>
                  {STATUS_ANZEIGE[ContentStatus.REJECTED]?.text ?? ContentStatus.REJECTED}
                </option>
              )}
            </select>
            {!darfModerieren && (
              <ModerationsHinweis id={`${feldId}-status-hinweis`} warnung={rückfall}>
                {rückfall ? MODERATIONS_TEXTE.rückfallHinweis : MODERATIONS_TEXTE.statusHinweis}
              </ModerationsHinweis>
            )}
          </div>
          <div className="md:pt-9">
            <PremiumFeld wert={formData.isPremium} onÄndern={(wert) => set('isPremium', wert)} gesperrt={!darfModerieren} />
          </div>
        </div>
      </div>

      {/* Aktionen */}
      <div className="fixed bottom-0 right-0 left-64 bg-white border-t border-secondary-200 px-6 py-4 flex justify-end gap-3 z-10">
        {/* Löschen ist Moderation — siehe `AudioForm`, BACKLOG `cms-listenknoepfe-redaktion`. */}
        {mindBookId && onDelete && darfModerieren && (
          <button type="button" onClick={() => onDelete(mindBookId)} className="btn btn-outline text-error-600 mr-auto">
            <TrashIcon className="w-5 h-5 mr-2" /> MindBook löschen
          </button>
        )}
        <button type="button" onClick={onCancel} className="btn btn-outline">Abbrechen</button>
        <button type="submit" disabled={isLoading} className="btn btn-primary">
          {isLoading ? 'Speichern...' : 'MindBook speichern'}
        </button>
      </div>

      <BunnyFilePicker
        isOpen={pickerOffen}
        onClose={() => setPickerOffen(false)}
        onSelect={handleBunnySelect}
        fileType="image"
        title="Cover-Bild wählen"
      />
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />
    </form>
  );
};
